import { ReactNode, useCallback, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useManualTasks } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTasks'
import { ManualTask } from 'Automations/watchtower/inbox/manualTasks/types'
import { optimisticallyViewedTaskIdsAtom } from 'Automations/watchtower/inbox/manualTasks/manualTasksInbox/atoms'
import { watchtowerFiltersAtom } from 'Automations/watchtower/atoms'
import { prefetchExecutionSummary } from 'Automations/watchtower/inbox/prefetchExecutionSummary'
import { useShouldUseAdminManualTasks } from 'Automations/watchtower/inbox/manualTasks/hooks/useShouldUseAdminManualTasks'
import { getManualTaskStatusIcon } from './getManualTaskStatusIcon'

export const useManualTaskListItemContentToolkit = <TInput>({
  taskId
}: {
  taskId: string
}): {
  manualTask: ManualTask<TInput> | undefined
  isUnread: boolean
  isPending: boolean
  statusIcon: ReactNode
  /**
   * Warms the data the task's preview needs, for use as the list item's
   * `onPreload`. Defaults to the execution summary the task opens into;
   * task types whose preview reads more (e.g. the email review) can wrap this.
   */
  preload: () => Promise<unknown>
} => {
  /**
   * A list item should not be making a call to the individual manual task, to avoid
   * an N+1 query scenario. We instead use the `useManualTasks` hook to get all tasks
   * and select the task from that response.
   *
   * Given that the list item will only be used in conjunction with this hook/list, this
   * is acceptable. If this assumption changes, we will need to refactor this hook!
   */
  const filters = useAtomValue(watchtowerFiltersAtom)
  const manualTaskStatus = useMemo(
    () => (filters.status.length > 0 ? filters.status.join(',') : undefined),
    [filters.status]
  )
  const { manualTasks, isPending } = useManualTasks({
    status: manualTaskStatus
  })

  const manualTask = useMemo(
    () => manualTasks.find(task => task.id === taskId),
    [manualTasks, taskId]
  ) as ManualTask<TInput> | undefined

  const optimisticallyViewedTaskIds = useAtomValue(
    optimisticallyViewedTaskIdsAtom
  )

  const isUnread = useMemo(() => {
    if (!manualTask) {
      return false
    }
    return !manualTask.viewedAt && !optimisticallyViewedTaskIds.has(taskId)
  }, [manualTask, optimisticallyViewedTaskIds, taskId])

  const statusIcon = useMemo(
    () => getManualTaskStatusIcon(manualTask?.status),
    [manualTask?.status]
  )

  const adminView = useShouldUseAdminManualTasks()
  const executionId = manualTask?.executorWorkflowExecutionId
  const preload = useCallback(
    () =>
      executionId
        ? prefetchExecutionSummary({
            executionId,
            manualTaskId: taskId,
            adminView
          })
        : Promise.resolve(),
    [executionId, taskId, adminView]
  )

  return {
    manualTask,
    isPending,
    isUnread,
    statusIcon,
    preload
  }
}
